import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, CheckCircle2, XCircle, Sparkles, ExternalLink, Loader2 } from "lucide-react";

const PLAN_ORDER = ["presencia", "club", "total"];
const PLAN_LABEL = { presencia: "Presencia", club: "Club", total: "Total" };
const PLAN_DESC = {
  presencia: "Ideal para empezar. Análisis básico, hasta 3 equipos.",
  club: "El más elegido. Todo Sudefootball para el club completo.",
  total: "Sin límites. Todos los módulos + soporte prioritario.",
};

function fmt(amount) {
  if (amount == null) return "—";
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(amount);
}

export default function BillingPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [config, setConfig] = useState(null);
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutBusy, setCheckoutBusy] = useState(null);
  const [portalBusy, setPortalBusy] = useState(false);
  const [interval, setInterval] = useState("month");

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([
        api.get("/billing/config"),
        api.get("/billing/subscription"),
      ]);
      setConfig(c.data);
      setSub(s.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    // ?session_id= after Checkout success — poll status until paid or timeout
    const sid = params.get("session_id");
    if (!sid) return;
    let cancelled = false;
    const poll = async (attempt = 0) => {
      try {
        const { data } = await api.get(`/billing/status/${sid}`);
        if (data.payment_status === "paid") {
          toast.success("¡Suscripción activada!");
          await reload();
          nav("/billing", { replace: true });
          return;
        }
      } catch (e) { /* ignore */ }
      if (!cancelled && attempt < 20) setTimeout(() => poll(attempt + 1), 1500);
    };
    poll();
    return () => { cancelled = true; };
  }, [params, nav, reload]);

  const startCheckout = async (plan) => {
    setCheckoutBusy(plan);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan,
        interval,
        apply_promo: true,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setCheckoutBusy(null);
    }
  };

  const changePlan = async (plan) => {
    const ok = window.confirm(
      `¿Cambiar la suscripción actual al plan ${PLAN_LABEL[plan]} (${interval === "year" ? "anual" : "mensual"})?\n\n` +
      `Stripe prorratea automáticamente la diferencia sobre lo que ya has pagado — no se hace ningún cobro extra en este momento.`
    );
    if (!ok) return;
    setCheckoutBusy(plan);
    try {
      const { data } = await api.post("/billing/change-plan", { plan, interval });
      toast.success(`Plan actualizado a ${PLAN_LABEL[plan]}. Próxima renovación: ${data.next_invoice_at || "—"}`);
      await reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCheckoutBusy(null);
    }
  };

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setPortalBusy(false);
    }
  };

  if (loading) {
    return <div className="p-10 flex items-center gap-3 text-sm text-muted-foreground"><Loader2 className="animate-spin" size={18}/> Cargando facturación…</div>;
  }
  if (!config?.enabled) {
    return <div className="p-10 text-sm">La facturación online no está configurada. Contacta con Sudefootball.</div>;
  }

  const activeSub = sub?.subscription;
  const catalog = config.catalog || {};

  // Plan Familias (B2C): sin planes B2B — solo estado de la suscripción familiar.
  if (sub?.plan === "familia") {
    return (
      <div className="max-w-3xl">
        <div className="mb-8">
          <div className="overline">Facturación</div>
          <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tighter uppercase mt-2">Tu suscripción</h1>
        </div>
        <div className={`border-2 p-5 flex items-center justify-between gap-4 flex-wrap ${sub?.plan_status === "active" ? "border-emerald-500 bg-emerald-500/10" : "border-amber-400 bg-amber-400/10"}`} data-testid="familia-subscription-card">
          <div className="flex items-center gap-3">
            <CheckCircle2 className={sub?.plan_status === "active" ? "text-emerald-400" : "text-amber-300"} />
            <div>
              <div className="font-heading font-bold uppercase text-sm tracking-tight">
                Sudefootball Player · {sub?.plan_status === "active" ? "Activa" : sub?.plan_status}
              </div>
              <div className="text-xs opacity-80 mt-0.5">
                12€ al año por jugador (IVA incluido) · pago anual
                {activeSub?.current_period_end && ` · Próxima renovación ${new Date(activeSub.current_period_end * 1000).toLocaleDateString("es-ES")}`}
                {activeSub?.cancel_at_period_end && " · Se cancelará al final del periodo"}
              </div>
            </div>
          </div>
          {activeSub && (
            <button data-testid="open-portal-btn" onClick={openPortal} disabled={portalBusy}
              className="inline-flex items-center gap-2 h-9 px-4 border-2 border-emerald-400 text-emerald-300 text-xs uppercase font-heading tracking-wider hover:bg-emerald-500/20 disabled:opacity-60">
              {portalBusy ? <Loader2 className="animate-spin" size={12}/> : <ExternalLink size={12}/>}
              Gestionar / cancelar
            </button>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-4">
          ¿Quieres añadir otro jugador a tu cuenta o tienes cualquier duda? Escríbenos a{" "}
          <a href="mailto:sudefootball@sudeporte.com" className="text-primary hover:underline">sudefootball@sudeporte.com</a> y lo gestionamos al momento.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl">
      <div className="mb-8">
        <div className="overline">Facturación</div>
        <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tighter uppercase mt-2">
          Suscripción y planes
        </h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Precios <b>con IVA incluido (21%)</b>. Stripe solo procesa el pago con su comisión base — sin recargos por gestión fiscal.
          Precio de lanzamiento: lo que ves es lo que pagas, <b className="text-primary">sin sorpresas ni subidas ocultas</b>. Te respetamos el precio durante 24 meses.
        </p>
      </div>

      {/* Subscription status card */}
      {activeSub ? (
        <div className="mb-8 border-2 border-emerald-500 bg-emerald-500/10 p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="active-subscription-card">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="text-emerald-400" />
            <div>
              <div className="font-heading font-bold uppercase text-sm tracking-tight">
                {activeSub.product_name || PLAN_LABEL[sub?.plan] || "Plan"} · {activeSub.status}
              </div>
              <div className="text-xs opacity-80 mt-0.5">
                {fmt(activeSub.amount)} / {activeSub.interval === "year" ? "año" : "mes"}
                {activeSub.discount && ` · -${activeSub.discount.percent_off}% durante ${activeSub.discount.months_remaining || 12} meses`}
                {activeSub.current_period_end && ` · Próxima renovación ${new Date(activeSub.current_period_end * 1000).toLocaleDateString("es-ES")}`}
                {activeSub.cancel_at_period_end && " · Se cancelará al final del periodo"}
              </div>
            </div>
          </div>
          <button data-testid="open-portal-btn" onClick={openPortal} disabled={portalBusy}
            className="inline-flex items-center gap-2 h-9 px-4 border-2 border-emerald-400 text-emerald-300 text-xs uppercase font-heading tracking-wider hover:bg-emerald-500/20 disabled:opacity-60">
            {portalBusy ? <Loader2 className="animate-spin" size={12}/> : <ExternalLink size={12}/>}
            Gestionar facturación
          </button>
        </div>
      ) : (
        <div className="mb-8 border-2 border-amber-400 bg-amber-400/10 p-5" data-testid="no-subscription-card">
          <div className="flex items-center gap-3">
            <Sparkles className="text-amber-300" />
            <div>
              <div className="font-heading font-bold uppercase text-sm tracking-tight">
                Aún sin suscripción · Prueba gratuita activa
              </div>
              <div className="text-xs opacity-80 mt-0.5">
                Elige un plan abajo. Es <b>precio de lanzamiento</b>: te lo respetamos durante 24 meses aunque suban las tarifas. Los días de prueba restantes se respetan como periodo de prueba de Stripe.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Interval toggle */}
      <div className="mb-6 inline-flex border-2 border-white/10" role="tablist">
        <button data-testid="interval-month" onClick={() => setInterval("month")}
          className={`h-9 px-5 text-xs uppercase font-heading tracking-wider ${interval === "month" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-white"}`}>
          Mensual
        </button>
        <button data-testid="interval-year" onClick={() => setInterval("year")}
          className={`h-9 px-5 text-xs uppercase font-heading tracking-wider ${interval === "year" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-white"}`}>
          Anual <span className="ml-1 text-emerald-400">1 mes gratis</span>
        </button>
      </div>

      {/* Plans grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {PLAN_ORDER.map((planKey) => {
          const p = catalog[planKey]?.[interval];
          const isCurrent = sub?.plan === planKey && sub?.plan_status === "active";
          const hasActiveSub = !!activeSub;
          const isSwitch = hasActiveSub && !isCurrent;
          const isMiddle = planKey === "club";
          if (!p) return null;
          const netAmount = p.amount;
          return (
            <div key={planKey} data-testid={`plan-card-${planKey}`}
              className={`relative border-2 p-6 flex flex-col ${isMiddle ? "border-primary" : "border-white/15"} ${isCurrent ? "bg-emerald-500/5" : ""}`}>
              {isMiddle && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-[10px] font-heading uppercase tracking-widest px-3 py-1">Más elegido</div>
              )}
              <div className="overline">{p.product_name || PLAN_LABEL[planKey]}</div>
              <div className="font-heading font-black text-3xl tracking-tighter uppercase mt-2">{PLAN_LABEL[planKey]}</div>
              <p className="text-xs text-muted-foreground mt-1 mb-5">{PLAN_DESC[planKey]}</p>

              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-3xl font-heading font-black">{fmt(netAmount)}</span>
                <span className="text-xs text-muted-foreground">/ {interval === "year" ? "año" : "mes"}</span>
              </div>
              <div className="flex items-center gap-2 text-[11px] mb-6">
                <span className="bg-primary/20 text-primary font-heading uppercase tracking-widest px-2 py-0.5">Precio de lanzamiento</span>
                {interval === "year" && <span className="text-muted-foreground">1 mes gratis</span>}
              </div>

              <button
                data-testid={`${isSwitch ? "change-plan-btn" : "checkout-btn"}-${planKey}`}
                onClick={() => (isSwitch ? changePlan(planKey) : startCheckout(planKey))}
                disabled={checkoutBusy === planKey || isCurrent}
                className={`h-10 text-xs uppercase font-heading tracking-widest flex items-center justify-center gap-2 ${isMiddle ? "bg-primary text-primary-foreground hover:bg-primary/80" : "border-2 border-white/20 hover:border-white/50"} disabled:opacity-60`}
              >
                {checkoutBusy === planKey ? <Loader2 className="animate-spin" size={12}/> : <CreditCard size={12}/>}
                {isCurrent
                  ? "Plan actual"
                  : isSwitch
                    ? `Cambiar a ${PLAN_LABEL[planKey]}`
                    : `Suscribirse · ${PLAN_LABEL[planKey]}`}
              </button>
              <div className="text-[10px] text-muted-foreground mt-3">
                {isSwitch ? "Prorrateo automático · Sin cobros extra ahora" : "IVA incluido · Cancela cuando quieras"}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-10 text-[11px] text-muted-foreground border-t border-white/10 pt-6 space-y-1">
        <div>• Pago seguro procesado por Stripe. Aceptamos tarjeta y otros métodos disponibles en tu país.</div>
        <div>• Precio de lanzamiento: te lo respetamos durante 24 meses aunque las tarifas suban. Pago anual = 12 meses al precio de 11.</div>
        <div>• Puedes descargar facturas, cambiar de plan o cancelar desde &quot;Gestionar facturación&quot;.</div>
      </div>
    </div>
  );
}


export function BillingSuccessPage() {
  const nav = useNavigate();
  useEffect(() => {
    const t = setTimeout(() => nav("/billing" + window.location.search, { replace: true }), 400);
    return () => clearTimeout(t);
  }, [nav]);
  return (
    <div className="p-10 flex items-center gap-3 text-sm">
      <CheckCircle2 className="text-emerald-400"/> Procesando el pago…
    </div>
  );
}


export function BillingCancelPage() {
  const nav = useNavigate();
  return (
    <div className="p-10">
      <div className="flex items-center gap-3 mb-4">
        <XCircle className="text-amber-400"/>
        <h1 className="font-heading font-black text-2xl uppercase tracking-tight">Pago cancelado</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-6">No se ha cobrado nada. Puedes volver a intentarlo cuando quieras.</p>
      <button onClick={() => nav("/billing", { replace: true })} className="h-10 px-5 border-2 border-white/20 hover:border-white/50 text-xs uppercase font-heading tracking-widest">
        Volver a facturación
      </button>
    </div>
  );
}
